/*
 * What every Inertia app in this codebase does once it exists:
 * report render errors to the server, and recover from a deploy that
 * moved the chunks out from under an open tab.
 *
 * Shared by the dashboard (app.js) and a customer's public website
 * (site.js), which are separate entries so that a club's visitors do
 * not download the operator app — and which must not drift apart on
 * either of these, because both fail invisibly when they are missing.
 */

/** Forward Vue errors to the server log. */
export function reportClientErrors(app) {
    // Forward Vue render errors to the backend so they land in
    // laravel.log alongside the user agent + URL. Without this
    // a blank-page render error in production is invisible to
    // the operator. The endpoint is best-effort; never let a
    // logging failure block the original error.
    app.config.errorHandler = (err, _instance, info) => {
        try {
            console.error('[Vue]', err, info);
            const csrfMeta = document.querySelector('meta[name="csrf-token"]');
            const csrf = csrfMeta ? csrfMeta.getAttribute('content') : '';
            fetch('/api/v1/client-errors', {
                method: 'POST',
                keepalive: true,
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': csrf,
                },
                body: JSON.stringify({
                    message: String(err && err.message ? err.message : err),
                    stack: String(err && err.stack ? err.stack : ''),
                    info: String(info || ''),
                    url: window.location.href,
                    ua: navigator.userAgent,
                }),
            }).catch(() => {});
        } catch (_) {
            // Logging is best-effort; never re-throw.
        }
    };
}

/** Finish a navigation on a fresh build when its chunk has gone. */
export function recoverFromStaleBundles(router) {
    // Stale-bundle recovery. Every Vite build hashes chunk
    // filenames (Trails-abc123.js becomes Trails-xyz789.js).
    // After a deploy, a client that was loaded pre-deploy still
    // holds the old chunk map — the first navigation that tries
    // to lazy-load a route the user hasn't visited yet 404s the
    // old chunk and the dynamic-import promise rejects silently.
    // Visible symptom: "clicks stop working, refresh fixes it".
    //
    // Catch the specific rejection shape and hard-navigate once —
    // TO THE PAGE THE USER CLICKED, not to the one they are on.
    //
    // location.reload() here was the swallowed-first-click bug:
    // the chunk import fails BEFORE Inertia commits the new URL
    // to history, so a reload re-fetched the page the user was
    // sitting on and threw their click away. The progress bar
    // had already run to completion (the XHR succeeded; only the
    // component import failed), which made it look like a
    // navigation that "did nothing". We stash the target of
    // every Inertia GET visit below, and finish that visit on
    // the fresh bundle with a full-page load.
    //
    // With the dashboard back on real Inertia versioning (see
    // HandleInertiaRequests::version), a post-deploy click 409s
    // and hard-visits the target before any import can fail —
    // this handler is now the safety net for the surfaces still
    // pinned to 'stable' (the PWA shells) and for any other way
    // a chunk can go missing.
    //
    // Guard against a loop: if we've already tried within the
    // last 30 s, don't retry — the 404 is probably from
    // something else (a deleted asset, a CDN miss) and looping
    // won't help.
    let lastIntendedVisit = null;
    router.on('before', (event) => {
        const visit = event.detail?.visit;
        if (! visit || String(visit.method).toLowerCase() !== 'get') return;
        try {
            lastIntendedVisit = { url: visit.url?.href || String(visit.url), at: Date.now() };
        } catch { /* leave the previous stash */ }
    });
    const chunkRegex = /Failed to fetch dynamically imported module|Loading chunk .* failed|error loading dynamically imported module/i;
    const shouldReload = (msg) => chunkRegex.test(String(msg || ''));
    const tryStaleBundleReload = (source, err) => {
        if (! shouldReload(err?.message ?? err)) return false;
        try {
            const last = Number(sessionStorage.getItem('tiq_stale_bundle_reload_at') || 0);
            if (Date.now() - last < 30_000) return false;
            sessionStorage.setItem('tiq_stale_bundle_reload_at', String(Date.now()));
            // A stash older than 15 s is not the visit that just
            // failed — fall back to a plain reload rather than
            // yanking the user to somewhere they clicked minutes
            // ago.
            const intended = lastIntendedVisit
                && (Date.now() - lastIntendedVisit.at < 15_000)
                ? lastIntendedVisit.url
                : null;
            if (intended && intended !== window.location.href) {
                console.warn('[stale-bundle]', source, '— finishing the clicked navigation on the new build');
                window.location.assign(intended);
            } else {
                console.warn('[stale-bundle]', source, '— reloading to pick up the new build');
                window.location.reload();
            }
            return true;
        } catch { return false; }
    };
    window.addEventListener('error', (e) => {
        // Script-load errors fire as ErrorEvent with the filename
        // in e.filename. Dynamic-import failures usually surface
        // through unhandledrejection below, but a raw <script>
        // 404 (rare in Vite land) still hits this path.
        tryStaleBundleReload('error', e.error || e.message);
    });
    window.addEventListener('unhandledrejection', (e) => {
        tryStaleBundleReload('unhandledrejection', e.reason);
    });
}
